enum GaEvent {
    FormStart = "form_start",
    PageView = "page",
    ButtonClick = "button_click",
    FormInteraction = "form_interaction",
}

export enum Form {
    B2cQuoteRequest = "consumer_quote_request",
    CompanySelection = "company_selection",
}

export enum FormInteraction {
    View = "view",
    Submit = "submit",
}

export interface LeadTrackingInfo {
    lead_uuid: string;
}

interface FormStartEventData {
    event: GaEvent.FormStart;
    form_name: Form;
    form_id?: string;
    element_id?: string;
    lead?: LeadTrackingInfo;
}

interface ButtonClickEventData {
    event: GaEvent.ButtonClick;
    element_id: string;
    form_name?: Form;
    lead?: LeadTrackingInfo;
}

interface FormInteractionEventData {
    event: GaEvent.FormInteraction;
    form_interaction: FormInteraction;
    form_name: Form;
    form_step: string;
    form_step_name: string;
    lead?: LeadTrackingInfo;
}

export interface PageViewEventData {
    event: GaEvent.PageView;
    page_location: string;
    audience_type: string;
    page_type: string;
    page_template: string;
    locale: string;
    business_unit: string;
    brand_id: number;
    brand_name: string;
    business_category?: string | null;
    business_category_id?: number | null;
    business_category_sub?: string | null;
    business_category_sub_id?: number | null;
    ab_test_variant?: string;
}

export type PageViewTrackingData = Omit<PageViewEventData, 'event' | 'page_location' | 'ab_test_variant'>;

export class GATracker {
    public static sendPageViewEvent(trackingData: PageViewTrackingData): void {
        const data: PageViewEventData = {
            event: GaEvent.PageView,
            page_location: window.location.href,
            ...trackingData,
        };

        const abVersion = document.body.getAttribute('data-ab');
        if (abVersion) {
            data.ab_test_variant = abVersion;
        }

        this.pushEvent(data);
    }

    public static sendFormStartEvent(elementOrData?: HTMLElement | Omit<FormStartEventData, 'event'> | null): void {
        if (elementOrData != null && !(elementOrData instanceof HTMLElement)) {
            this.pushEvent({ event: GaEvent.FormStart, ...elementOrData });

            return;
        }

        const formElement = document.querySelector('[data-authcode]') as HTMLElement;

        if (!formElement) {
            return;
        }

        const formStartEventData: FormStartEventData = {
            event: GaEvent.FormStart,
            form_id: formElement.getAttribute('data-authcode'),
            form_name: Form.B2cQuoteRequest,
        };

        if (elementOrData instanceof HTMLElement && elementOrData.id) {
            formStartEventData.element_id = elementOrData.id;
        }

        this.pushEvent(formStartEventData);
    }

    public static sendFormInteractionEvent(data: Omit<FormInteractionEventData, 'event'>): void {
        this.pushEvent({ event: GaEvent.FormInteraction, ...data });
    }

    public static sendButtonClickEvent(data: Omit<ButtonClickEventData, 'event'>): void {
        this.pushEvent({ event: GaEvent.ButtonClick, ...data });
    }

    private static pushEvent(event: Record<string, any>): void {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push(event);
        this.debugEvent(event);
    }

    private static debugEvent(event: Record<string, any>): void {
        try {
            if (!window.localStorage.getItem('ga-debug')) {
                return;
            }

            // eslint-disable-next-line no-console
            console.info('GA event pushed:', event);
        } catch(e) {
            // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
        }
    }
}

try {
    if (window.location.hash === '#enable-ga-debug') {
        window.localStorage.setItem('ga-debug', '1');
        // eslint-disable-next-line no-console
        console.info('GA debug enabled');
    }
} catch(e) {
    // Storage blocked in this context (e.g. Safari with cookies disabled, private mode)
}

(<any>window).GATracker = GATracker;
